import { LuUserPlus } from "react-icons/lu";
import { FaPlus, FaUsers } from "react-icons/fa6";
import {
  ProjectDetailsDocument,
  ProjectFragment,
  PublicUserOrganization,
  UpdateProjectParticipantsDocument,
  UserGroupFragment,
} from "../../../../../../__generated__/graphql";
import Button from "../../../../../../components/Button";
import { useState, useMemo } from "react";
import { toast } from "react-hot-toast";
import ParticipantsSelectionModal from "../../../../../../components/ParticipantsSelectionModal";
import UserOrganizationItem from "../../../../../../components/UserOrganizationItem";
import { useMutation } from "@apollo/client";
import ResourcesManagementModal from "../ResourcesManagementModal";
import useNav from "../../../../../../hooks/useNavigation";
import useUserGroups from "./hooks/useUserGroups";
import UserGroupCard from "./components/UserGroupCard";
import UserGroupModal from "./components/UserGroupModal";
import { PencilIcon, UserGroupIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useUser } from "../../../../../../contexts/UserContext/hooks/useUser";
import ConfirmDialog from "../../../../../../components/ConfirmDialog";

export default function ProjectParticipants({
  project,
  refetchProject,
}: {
  project: ProjectFragment;
  refetchProject?: () => void;
}) {
  const navigateWithLog = useNav();
  const { currentOrganization } = useUser();

  // Helper to check if current user can edit a group
  const canEditGroup = (group: UserGroupFragment) => {
    return group.organizationId === currentOrganization?.id;
  };

  const [isManageParticipantsModalOpen, setIsManageParticipantsModalOpen] =
    useState(false);
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isManageGroupsModalOpen, setIsManageGroupsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UserGroupFragment | null>(null);
  const [newlyAddedParticipants, setNewlyAddedParticipants] = useState<
    PublicUserOrganization[]
  >([]);
  const [pendingParticipantChanges, setPendingParticipantChanges] = useState<{
    selected: PublicUserOrganization[];
    removed: PublicUserOrganization[];
  } | null>(null);
  const [pendingGroupUnassign, setPendingGroupUnassign] = useState<UserGroupFragment | null>(null);

  const { userGroups, loading: loadingGroups } = useUserGroups();

  const [updateProjectParticipants, { loading: isUpdatingProjectParticipants }] =
    useMutation(UpdateProjectParticipantsDocument, {
      refetchQueries: [
        { query: ProjectDetailsDocument, variables: { projectId: project.id } },
      ],
    });

  // Get currently assigned group IDs
  const assignedGroupIds =
    project.projectGroupParticipants?.map((gp) => gp?.userGroup.id) ?? [];

  // Filter groups: show project-specific groups and organization-wide groups
  const { projectGroups, organizationGroups } = useMemo(() => {
    const projectGroups = userGroups.filter(
      (group) => group.projectId === project.id
    );
    const organizationGroups = userGroups.filter(
      (group) => !group.projectId && !group.eventId
    );
    return { projectGroups, organizationGroups };
  }, [userGroups, project.id]);

  // Combined groups to display in the modal
  const relevantGroups = useMemo(() => {
    return [...projectGroups, ...organizationGroups];
  }, [projectGroups, organizationGroups]);

  const handleAssignGroup = async (userGroup: UserGroupFragment) => {
    try {
      const currentParticipantIds =
        project.projectParticipants?.map((p) => p?.publicUserOrganization.id) ?? [];
      const currentGroupIds = assignedGroupIds.filter((id): id is string => !!id);

      await updateProjectParticipants({
        variables: {
          projectId: project.id,
          participantUserOrganizationIds: currentParticipantIds.filter(
            (id): id is string => !!id
          ),
          groupParticipantUserGroupIds: [...currentGroupIds, userGroup.id],
        },
      });

      toast.success(`Groupe "${userGroup.name}" assigné au projet`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Erreur lors de l'assignation"
      );
    }
  };

  const handleUnassignGroup = async (userGroup: UserGroupFragment) => {
    try {
      const currentParticipantIds =
        project.projectParticipants?.map((p) => p?.publicUserOrganization.id) ?? [];
      const currentGroupIds = assignedGroupIds.filter((id): id is string => !!id);

      await updateProjectParticipants({
        variables: {
          projectId: project.id,
          participantUserOrganizationIds: currentParticipantIds.filter(
            (id): id is string => !!id
          ),
          groupParticipantUserGroupIds: currentGroupIds.filter(
            (id) => id !== userGroup.id
          ),
        },
      });

      toast.success(`Groupe "${userGroup.name}" retiré du projet`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Erreur lors du retrait"
      );
    }
  };

  const applyParticipantChanges = async (
    selectedUserOrganizations: PublicUserOrganization[]
  ) => {
    if (!project?.id) return;

    try {
      const alreadyAddedParticipants = project.projectParticipants
        ?.map((participant) => participant?.publicUserOrganization)
        .filter((participant) => participant !== undefined);

      // Preserve current group assignments
      const currentGroupIds = assignedGroupIds.filter(
        (id): id is string => !!id
      );

      const updatedProject = await updateProjectParticipants({
        variables: {
          projectId: project.id,
          participantUserOrganizationIds: selectedUserOrganizations.map(
            (userOrganization) => userOrganization.id
          ),
          groupParticipantUserGroupIds: currentGroupIds,
        },
      });

      if (!updatedProject.data?.updateProjectParticipants) {
        toast.error(
          "Une erreur est survenue lors de la mise à jour des participants"
        );
        return;
      }

      const newlyAdded = selectedUserOrganizations.filter(
        (userOrganization) =>
          !alreadyAddedParticipants?.some((p) => p?.id === userOrganization.id)
      );

      setNewlyAddedParticipants(newlyAdded);
      setIsManageParticipantsModalOpen(false);
      setPendingParticipantChanges(null);
      
      toast.success("Participants mis à jour");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de la mise à jour des participants"
      );
    }
  };

  const participantList = () => {
    if (
      !project.projectParticipants ||
      project.projectParticipants.length === 0
    ) {
      return (
        <div className="text-sm text-tertiary">
          Aucun participant individuel
        </div>
      );
    }

    return (
      <div className="text-sm text-secondary grid grid-cols-2 gap-2">
        {project.projectParticipants?.map((participant) => {
          if (!participant) return null;
          return (
            <UserOrganizationItem
              key={participant.id}
              onClick={() => {
                navigateWithLog(
                  `/collaborators/${participant.publicUserOrganization.id}`
                );
              }}
              userOrganization={participant.publicUserOrganization}
            />
          );
        })}
      </div>
    );
  };

  const assignedGroupsList = () => {
    const assignedGroups = project.projectGroupParticipants?.filter(
      (gp): gp is NonNullable<typeof gp> => !!gp
    );

    if (!assignedGroups || assignedGroups.length === 0) {
      return (
        <div className="text-sm text-tertiary">Aucun groupe assigné</div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {assignedGroups.map((gp) => {
          const canEdit = canEditGroup(gp.userGroup);
          return (
            <div
              key={gp.id}
              className={`bg-card border border-border rounded-lg p-3 flex items-center justify-between gap-3 transition-colors ${
                canEdit ? "cursor-pointer hover:bg-card/80" : ""
              }`}
              onClick={() => canEdit && setEditingGroup(gp.userGroup)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <UserGroupIcon className="w-4 h-4 text-accent" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-primary truncate">
                    {gp.userGroup.name}
                  </div>
                  <div className="text-xs text-secondary">
                    {gp.userGroup.members?.length ?? 0} membre
                    {(gp.userGroup.members?.length ?? 0) > 1 ? "s" : ""}
                    {!canEdit && (
                      <span className="ml-1 text-tertiary">
                        · {gp.userGroup.createdByPublicUserOrganization?.organizationName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {canEdit && (
                  <button
                    className="p-2 hover:bg-border rounded-lg text-secondary hover:text-primary transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingGroup(gp.userGroup);
                    }}
                    title="Modifier le groupe"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                )}
                <button
                  className="p-2 hover:bg-red-500/20 rounded-lg text-secondary hover:text-red-400 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingGroupUnassign(gp.userGroup);
                  }}
                  title="Retirer le groupe du projet"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <ConfirmDialog
        isOpen={!!pendingParticipantChanges}
        onClose={() => setPendingParticipantChanges(null)}
        onConfirm={async () => {
          if (pendingParticipantChanges) {
            await applyParticipantChanges(pendingParticipantChanges.selected);
          }
        }}
        title="Retirer des participants"
        description={`Vous êtes sur le point de retirer ${pendingParticipantChanges?.removed.length ?? 0} participant${(pendingParticipantChanges?.removed.length ?? 0) > 1 ? "s" : ""} du projet. Ces personnes perdront l'accès au projet ainsi qu'à tous les évènements, activités, documents et groupes liés à ce projet.`}
        confirmText="Confirmer le retrait"
        cancelText="Annuler"
        variant="danger"
        isLoading={isUpdatingProjectParticipants}
      />

      <ConfirmDialog
        isOpen={!!pendingGroupUnassign}
        onClose={() => setPendingGroupUnassign(null)}
        onConfirm={async () => {
          if (pendingGroupUnassign) {
            await handleUnassignGroup(pendingGroupUnassign);
            setPendingGroupUnassign(null);
          }
        }}
        title="Retirer le groupe du projet"
        description={`Êtes-vous sûr de vouloir retirer le groupe "${pendingGroupUnassign?.name}" du projet ? Les membres de ce groupe perdront l'accès aux évènements, activités et documents liés à ce groupe.`}
        confirmText="Retirer le groupe"
        cancelText="Annuler"
        variant="danger"
        isLoading={isUpdatingProjectParticipants}
      />

      {newlyAddedParticipants.length > 0 ? (
        <ResourcesManagementModal
          project={project}
          participants={newlyAddedParticipants}
          isOpen={newlyAddedParticipants.length > 0}
          onClose={() => setNewlyAddedParticipants([])}
        />
      ) : null}

      <ParticipantsSelectionModal
        isOpen={isManageParticipantsModalOpen}
        onClose={() => setIsManageParticipantsModalOpen(false)}
        title="Gérer les participants"
        description="Modifiez la liste des participants pour ce projet."
        initialSelectedUsers={
          project.projectParticipants
            ?.map((participant) => participant?.publicUserOrganization)
            .filter(
              (participant): participant is PublicUserOrganization =>
                participant !== undefined && participant !== null
            ) ?? []
        }
        submitButtonText="Enregistrer les participants"
        onSubmit={async (selectedUserOrganizations) => {
          if (!project?.id) return;

          const currentParticipants =
            project.projectParticipants
              ?.map((participant) => participant?.publicUserOrganization)
              .filter(
                (participant): participant is PublicUserOrganization =>
                  participant !== undefined
              ) ?? [];

          // Check if any participants are being removed
          const removedParticipants = currentParticipants.filter(
            (current) =>
              !selectedUserOrganizations.some((selected) => selected.id === current.id)
          );

          if (removedParticipants.length > 0) {
            // Store pending changes and show confirmation
            setPendingParticipantChanges({
              selected: selectedUserOrganizations,
              removed: removedParticipants,
            });
            setIsManageParticipantsModalOpen(false);
            return;
          }

          // No removals, proceed directly
          await applyParticipantChanges(selectedUserOrganizations);
        }}
      />

      <UserGroupModal
        isOpen={isCreateGroupModalOpen}
        onClose={() => setIsCreateGroupModalOpen(false)}
        projectId={project.id}
        onGroupCreated={(group) => {
          // Automatically assign the newly created group to the project
          handleAssignGroup(group);
        }}
        onGroupUpdated={refetchProject}
      />

      {/* Edit Group Modal */}
      <UserGroupModal
        isOpen={!!editingGroup}
        onClose={() => setEditingGroup(null)}
        projectId={project.id}
        userGroup={editingGroup}
        onGroupUpdated={refetchProject}
      />

      {/* Manage Groups Modal */}
      {isManageGroupsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsManageGroupsModalOpen(false)}
          />
          <div data-testid="manage-groups-modal" className="relative bg-dark border border-input rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto custom-scrollbar m-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-primary">
                  Gérer les groupes
                </h2>
                <p className="text-sm text-secondary">
                  Créez, modifiez ou assignez des groupes au projet
                </p>
              </div>
              <button
                className="p-2 hover:bg-border rounded-lg text-secondary hover:text-primary"
                onClick={() => setIsManageGroupsModalOpen(false)}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {loadingGroups ? (
              <div className="text-sm text-tertiary">Chargement...</div>
            ) : relevantGroups.length === 0 ? (
              <div className="text-sm text-tertiary py-8 text-center">
                Aucun groupe créé. Créez votre premier groupe pour commencer.
              </div>
            ) : (
              <div className="space-y-6">
                {/* Project-specific groups */}
                {projectGroups.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-accent uppercase tracking-wider mb-3">
                      Groupes du projet ({projectGroups.length})
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {projectGroups.map((group) => (
                        <UserGroupCard
                          key={group.id}
                          userGroup={group}
                          isAssigned={assignedGroupIds.includes(group.id)}
                          onAssign={() => handleAssignGroup(group)}
                          onUnassign={() => handleUnassignGroup(group)}
                          canEdit={canEditGroup(group)}
                          projectId={project.id}
                          onGroupUpdated={refetchProject}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Organization-wide groups */}
                {organizationGroups.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-secondary uppercase tracking-wider mb-3">
                      Groupes de l'organisation ({organizationGroups.length})
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {organizationGroups.map((group) => (
                        <UserGroupCard
                          key={group.id}
                          userGroup={group}
                          isAssigned={assignedGroupIds.includes(group.id)}
                          onAssign={() => handleAssignGroup(group)}
                          onUnassign={() => handleUnassignGroup(group)}
                          canEdit={canEditGroup(group)}
                          projectId={project.id}
                          onGroupUpdated={refetchProject}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Footer with CTA */}
            <div className="flex justify-end pt-4 mt-4 border-t border-border">
              <Button
                dataTestId="create-new-group-button"
                className="gap-2"
                onClick={() => {
                  setIsManageGroupsModalOpen(false);
                  setIsCreateGroupModalOpen(true);
                }}
              >
                <FaPlus className="w-3 h-3" />
                Créer un nouveau groupe
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6 max-w-4xl">
        {/* Groups Section */}
        <div className="flex flex-col gap-4 border border-input rounded-md p-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-lg font-bold flex items-center gap-2">
                Groupes
              </div>
              <div className="text-sm text-secondary">
                Assignez des groupes de participants au projet
              </div>
            </div>

            <Button
              dataTestId="manage-groups-button"
              variant="secondary"
              className="text-sm w-fit flex-shrink-0 gap-2 items-center"
              onClick={() => setIsManageGroupsModalOpen(true)}
            >
              <FaUsers className="w-4 h-4" />
              Gérer les groupes
            </Button>
          </div>

          {assignedGroupsList()}
        </div>

        {/* Individual Participants Section */}
        <div className="flex flex-col gap-4 border border-input rounded-md p-4">
        <div className="flex justify-between items-center">
            <div>
              <div className="text-lg font-bold">Liste des participants</div>
              <div className="text-sm text-secondary">
                Tous les participants du projet
              </div>
            </div>

          <Button
            dataTestId="manage-participants-button"
            variant="secondary"
            className="text-sm w-fit flex-shrink-0 gap-2 items-center"
            onClick={() => {
              setIsManageParticipantsModalOpen(true);
            }}
            isLoading={isUpdatingProjectParticipants}
          >
            <LuUserPlus className="w-4 h-4" />
            Gérer les participants
          </Button>
        </div>

        {participantList()}
        </div>
      </div>
    </>
  );
}
